/**
 * Centralized, type-safe localStorage access for the AI assistant.
 */

const KEYS = {
  ASSISTANT_OPEN: 'ai_assistant_open',
  ASSISTANT_MINIMIZED: 'ai_assistant_minimized',
  ACTIVE_CONVERSATION: 'ai_active_conversation',
  DRAFT_MESSAGE: 'ai_draft_message',
  SHOWN_UPDATE_IDS: 'shown_system_update_ids',
} as const;

// ─── Getters ─────────────────────────────────────────────────────────

export function getAssistantOpen(): boolean {
  try { return localStorage.getItem(KEYS.ASSISTANT_OPEN) === 'true'; } catch { return false; }
}

export function getAssistantMinimized(): boolean {
  try {
    const v = localStorage.getItem(KEYS.ASSISTANT_MINIMIZED);
    return v !== null ? v === 'true' : true; // default minimized
  } catch { return true; }
}

export function getActiveConversation(): string | null {
  try { return localStorage.getItem(KEYS.ACTIVE_CONVERSATION) || null; } catch { return null; }
}

export function getDraftMessage(): string {
  try { return localStorage.getItem(KEYS.DRAFT_MESSAGE) || ''; } catch { return ''; }
}

export function getShownUpdateIds(): number[] {
  try { return JSON.parse(localStorage.getItem(KEYS.SHOWN_UPDATE_IDS) || '[]'); } catch { return []; }
}

// ─── Setters ─────────────────────────────────────────────────────────

export function setAssistantOpen(open: boolean): void {
  try { localStorage.setItem(KEYS.ASSISTANT_OPEN, open ? 'true' : 'false'); } catch {}
}

export function setAssistantMinimized(minimized: boolean): void {
  try { localStorage.setItem(KEYS.ASSISTANT_MINIMIZED, minimized ? 'true' : 'false'); } catch {}
}

export function setActiveConversation(id: string | null): void {
  try {
    if (id) localStorage.setItem(KEYS.ACTIVE_CONVERSATION, id);
    else localStorage.removeItem(KEYS.ACTIVE_CONVERSATION);
  } catch {}
}

export function setDraftMessage(text: string): void {
  try {
    if (text) localStorage.setItem(KEYS.DRAFT_MESSAGE, text);
    else localStorage.removeItem(KEYS.DRAFT_MESSAGE);
  } catch {}
}

export function markUpdatesAsShown(ids: number[]): void {
  try {
    const merged = [...new Set([...getShownUpdateIds(), ...ids])];
    localStorage.setItem(KEYS.SHOWN_UPDATE_IDS, JSON.stringify(merged));
  } catch {}
}

export function clearActiveConversation(): void {
  try { localStorage.removeItem(KEYS.ACTIVE_CONVERSATION); } catch {}
}
